import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { checkUserSummitRegistration, getUserSummitRegistration, getUserSummitRegistrationFromUser } from '../services/summitService';

const SummitContext = createContext();

export const useSummit = () => {
  const context = useContext(SummitContext);
  if (!context) {
    throw new Error('useSummit must be used within a SummitProvider');
  }
  return context;
};

export const SummitProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [userRegistration, setUserRegistration] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check user registration status
  const checkRegistration = async () => {
    if (!currentUser?.uid) {
      setUserRegistration(null);
      setIsRegistered(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // First try to get registration from user document (faster and more reliable)
      let registration = await getUserSummitRegistrationFromUser(currentUser.uid);
      
      // If not found in user document, fall back to summit_registrations collection
      if (!registration && currentUser.email) {
        registration = await getUserSummitRegistration(currentUser.email);
      }
      
      setUserRegistration(registration);
      setIsRegistered(!!registration);
    } catch (error) {
      console.error('Error checking registration status:', error);
      setUserRegistration(null);
      setIsRegistered(false);
    } finally {
      setLoading(false);
    }
  };

  // Refresh registration status
  const refreshRegistration = useCallback(async () => {
    await checkRegistration();
  }, []);

  // Update registration status after changes
  const updateRegistrationStatus = useCallback((registration) => {
    setUserRegistration(registration);
    setIsRegistered(!!registration);
  }, []);

  // Check registration when user changes
  useEffect(() => {
    checkRegistration();
  }, [currentUser?.uid]);

  const value = {
    userRegistration,
    isRegistered,
    loading,
    checkRegistration,
    refreshRegistration,
    updateRegistrationStatus
  };

  return (
    <SummitContext.Provider value={value}>
      {children}
    </SummitContext.Provider>
  );
};
